import { c as _c } from "react/compiler-runtime";
import type { WandAnalyticsParams } from '@repo/wemod-api-client/analytics/analytics-cookie';
import { parseWandAnalyticsCookie } from '@repo/wemod-api-client/analytics/analytics-cookie';
import { useCallback, useRef, useSyncExternalStore } from 'react';
type ParamsSnapshotCache = {
  serialized: string;
  snapshot: WandAnalyticsParams | null;
};
function serializeWandAnalyticsParams(params: WandAnalyticsParams | null): string {
  return JSON.stringify(params);
}
function readClientParams(analyticsParams: WandAnalyticsParams | null): WandAnalyticsParams | null {
  if (typeof document === 'undefined') {
    return analyticsParams;
  }
  return parseWandAnalyticsCookie(document.cookie) ?? analyticsParams;
}
function getCachedSnapshot(cache: ParamsSnapshotCache, analyticsParams: WandAnalyticsParams | null): WandAnalyticsParams | null {
  const next = readClientParams(analyticsParams);
  const serialized = serializeWandAnalyticsParams(next);
  if (serialized === cache.serialized) {
    return cache.snapshot;
  }
  cache.serialized = serialized;
  cache.snapshot = next;
  return cache.snapshot;
}
function getCachedInitialSnapshot(cache: ParamsSnapshotCache, analyticsParams: WandAnalyticsParams | null): WandAnalyticsParams | null {
  const serialized = serializeWandAnalyticsParams(analyticsParams);
  if (serialized === cache.serialized) {
    return cache.snapshot;
  }
  cache.serialized = serialized;
  cache.snapshot = analyticsParams;
  return cache.snapshot;
}
function subscribe(onStoreChange: () => void): () => void {
  if (typeof document === 'undefined') {
    return () => undefined;
  }
  const onVisibilityChange = (): void => {
    onStoreChange();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
export function useWandAnalyticsParams(analyticsParams, eligibilityKey) {
  const $ = _c(14);
  let t0;
  if ($[0] !== analyticsParams) {
    t0 = serializeWandAnalyticsParams(analyticsParams);
    $[0] = analyticsParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== analyticsParams || $[3] !== t0) {
    t1 = {
      serialized: t0,
      snapshot: analyticsParams
    };
    $[2] = analyticsParams;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const clientCacheRef = useRef(t1);
  let t2;
  if ($[5] !== analyticsParams) {
    t2 = serializeWandAnalyticsParams(analyticsParams);
    $[5] = analyticsParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== analyticsParams || $[8] !== t2) {
    t3 = {
      serialized: t2,
      snapshot: analyticsParams
    };
    $[7] = analyticsParams;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const serverCacheRef = useRef(t3);
  let t4;
  if ($[10] !== analyticsParams) {
    t4 = () => getCachedSnapshot(clientCacheRef.current, analyticsParams);
    $[10] = analyticsParams;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const getSnapshot = t4;
  let t5;
  if ($[12] !== analyticsParams) {
    t5 = () => getCachedInitialSnapshot(serverCacheRef.current, analyticsParams);
    $[12] = analyticsParams;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const getServerSnapshot = t5;
  const subscribeWithKey = _temp;
  return useSyncExternalStore(subscribeWithKey, getSnapshot, getServerSnapshot);
}
function _temp(onStoreChange) {
  return subscribe(onStoreChange);
}